import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { Formik, FastField, Form } from 'formik';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TagSelectorDropDown from '@/trio-ui/TagSelector';
import TransparentButton from '@/trio-ui/TransparentButton';
import { ValuesWithLabel } from '@/types';
import { getYearsFromNowList } from '@/utils/dates';
import { buildErrorMessage, candidateProjectFormSchema } from '@/utils/formsValidation';
import * as Styles from './CandidateProject.styles';
import TextEditor from '../TextEditor';

export type CandidateProjectComponentProps = {
  isSubmitting: boolean;
  id?: string;
  title: string;
  link: string;
  description: string;
  year: string;
  skills: string[];
  onSaveProject: (values: any) => void;
  onCancelProjectEdition: () => void;
  skillsOptions: ValuesWithLabel<string>[];
};

export default function CandidateProjectComponent({
  title,
  link,
  id,
  description,
  year,
  skills,
  onSaveProject,
  onCancelProjectEdition,
  skillsOptions,
  isSubmitting,
}: CandidateProjectComponentProps): JSX.Element {
  const yearsOptions = getYearsFromNowList();

  return (
    <Styles.Box>
      <Formik
        validationSchema={candidateProjectFormSchema}
        initialValues={{ title, link, year, id, skills, description }}
        onSubmit={(values) => onSaveProject(values)}
      >
        {(form) => {
          return (
            <Form>
              <Styles.Fieldset disabled={isSubmitting}>
                <Styles.InputWrapper isHalf>
                  <FastField name="title">
                    {({ field }) => {
                      return (
                        <InputText
                          value={field.value}
                          name="title"
                          errorMessage={buildErrorMessage(form.touched.title, form.errors.title)}
                          label="Project Title"
                          onChangeHandler={form.handleChange}
                          isLabelAboveField
                        />
                      );
                    }}
                  </FastField>
                </Styles.InputWrapper>
                <Styles.InputWrapper isHalf>
                  <FastField name="link">
                    {({ field }) => {
                      return (
                        <InputText
                          value={field.value}
                          name="link"
                          label="Project Link"
                          errorMessage={buildErrorMessage(form.touched.link, form.errors.link)}
                          onChangeHandler={form.handleChange}
                          isLabelAboveField
                        />
                      );
                    }}
                  </FastField>
                </Styles.InputWrapper>
                <Styles.InputWrapper isHalf>
                  <FastField name="year">
                    {({ field }) => {
                      return (
                        <DropDown
                          label="Year"
                          name="year"
                          isLabelAboveField
                          errorMessage={buildErrorMessage(form.touched.year, form.errors.year)}
                          defaultValue={field.value}
                          onChangeDropDown={form.handleChange}
                          options={yearsOptions}
                        />
                      );
                    }}
                  </FastField>
                </Styles.InputWrapper>
                <Styles.InputWrapper>
                  <FastField name="skills">
                    {({ field }) => {
                      return (
                        <div style={{ marginBottom: '16px' }}>
                          <TagSelectorDropDown
                            label="Technologies Used"
                            name="skills"
                            errorMessage={buildErrorMessage(
                              form.touched.skills,
                              form.errors.skills
                            )}
                            isLabelAboveField
                            selectorLimit={7}
                            defaultValue={field.value}
                            onChangeDropDown={form.handleChange}
                            optionsWithLabel={skillsOptions}
                          />
                        </div>
                      );
                    }}
                  </FastField>
                </Styles.InputWrapper>
                <Styles.InputWrapper>
                  <FastField name="description">
                    {({ field }) => {
                      return (
                        <div>
                          <Styles.TextAreaLabel>Description</Styles.TextAreaLabel>
                          <TextEditor
                            errorMessage={buildErrorMessage(
                              form.touched.description,
                              form.errors.description
                            )}
                            isDisabled={form.isSubmitting}
                            value={field.value}
                            onChangeText={(e) => {
                              return form.handleChange({
                                type: 'change',
                                target: {
                                  value: e,
                                  name: 'description',
                                },
                              });
                            }}
                            isEditing
                            hideButtons
                            hideEditButton
                          />
                        </div>
                      );
                    }}
                  </FastField>
                </Styles.InputWrapper>

                <Styles.Footer>
                  <TransparentButton
                    isBlueButton
                    disabled={isSubmitting}
                    type="submit"
                    icon={SaveIcon}
                  >
                    Save
                  </TransparentButton>
                  <TransparentButton
                    icon={DeleteIcon}
                    disabled={isSubmitting}
                    isRedButton
                    onClick={() => {
                      onCancelProjectEdition();
                    }}
                  >
                    Cancel
                  </TransparentButton>
                </Styles.Footer>
              </Styles.Fieldset>
            </Form>
          );
        }}
      </Formik>
    </Styles.Box>
  );
}
